import { createContext, useContext, useMemo } from "react";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { createBrowserRouter, Outlet, RouterProvider, ScrollRestoration } from "react-router-dom";
import { FaqContextProvider } from "../context/FaqContext.jsx";
import { JobsContextProvider } from "../context/JobsContext";
import { LayoutContextProvider } from "../context/LayoutContext";
import { MachineContextProvider } from "../context/MachineContext";
import { NewsContextProvider } from "../context/NewsContext.jsx";
import { SiteContentContext, SiteContentProvider } from "../context/SiteContentContext.jsx";
import { TermsContextProvider } from "../context/TermsContext.jsx";
import { UserContext, UserContextProvider } from "../context/UserContext";
import { adminRoutes, publicRoutes } from "../routeComponentMappings";
import ErrorPage from "../routes/errorPage";
import CookieConsent from "./cookieConsent";
import Footer from "./footer";
import Header from "./header";
import LocaleLayout from "./localeLayout.jsx";
import LocaleRedirect from "./localeRedirect.jsx";
import StickyContactButton from "./stickyContactButton.jsx";

function AdminOverlayToggle() {
  const { hasRole } = useContext(UserContext);
  const { overlayActive, setOverlayActive } = useContext(SiteContentContext);

  if (!hasRole("admin")) return null;

  return (
    <button
      type="button"
      onClick={() => setOverlayActive((prev) => !prev)}
      className={`fixed bottom-4 left-4 z-[9999] flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold shadow-lg transition-colors ${
        overlayActive
          ? "bg-amber-400 text-amber-900 hover:bg-amber-300"
          : "bg-gray-800/80 text-gray-200 hover:bg-gray-700"
      }`}
      title={overlayActive ? "Dölj innehållsnycklar" : "Visa innehållsnycklar"}
    >
      <span>⚿</span>
      <span>{overlayActive ? "Dölj nycklar" : "Visa nycklar"}</span>
    </button>
  );
}

/**
 * True while entry-server.jsx is rendering on the server. The static render has no data
 * router (it goes through StaticRouter), so router features that require one must opt out.
 */
export const StaticRenderContext = createContext(false);

const WrapperElement = () => {
  const staticRender = useContext(StaticRenderContext);

  return (
    <div data-testid="wrapper-element">
      {/* ScrollRestoration throws outside a data router, and there is nothing to restore in a
          server render anyway. */}
      {!staticRender && <ScrollRestoration />}
      <Header />
      <div className="outlet-container pt-16">
        <Outlet />
      </div>
      <CookieConsent />
      <Footer />
      <StickyContactButton />
      <AdminOverlayToggle />
    </div>
  );
};

const toRouteObjects = (routes, stripLeadingSlash = false) =>
  routes.map(({ path, element, resolveBreadcrumb }) => {
    const adjustedPath = stripLeadingSlash && path.startsWith("/") ? path.slice(1) : path;
    const routeObj = {
      element,
      handle: { resolveBreadcrumb },
    };
    if (adjustedPath === "") {
      routeObj.index = true;
    } else {
      routeObj.path = adjustedPath;
    }
    return routeObj;
  });

export const routeConfig = [
  {
    path: "/",
    element: <WrapperElement />,
    errorElement: <ErrorPage />,
    children: [
      {
        index: true,
        element: <LocaleRedirect />,
      },
      {
        path: ":locale",
        element: <LocaleLayout />,
        children: toRouteObjects(publicRoutes, true),
      },
      ...toRouteObjects(adminRoutes),
      {
        path: "*",
        element: <LocaleRedirect />,
      },
    ],
  },
];

/**
 * Every provider the app needs, with no router inside — the client mounts a browser router
 * under this, entry-server.jsx mounts a static one. Keep them identical: anything added here
 * is automatically part of the server-rendered HTML too.
 *
 * @param {string} [initialLocale] locale of the page being rendered (server render only)
 * @param {object} [helmetContext] react-helmet-async collector (server render only)
 */
export function AppProviders({ initialLocale, helmetContext, staticRender = false, children }) {
  return (
    <StaticRenderContext.Provider value={staticRender}>
      <LayoutContextProvider>
        <UserContextProvider initialLocale={initialLocale}>
          <MachineContextProvider>
            <JobsContextProvider>
              <NewsContextProvider>
                <FaqContextProvider>
                  <TermsContextProvider>
                    <SiteContentProvider>
                      <HelmetProvider context={helmetContext}>
                        <Helmet>
                          {/* Default Open Graph tags - overridden by page-specific Helmet */}
                          <meta property="og:site_name" content="AS Maskiner" />
                          <meta property="og:type" content="website" />
                          <meta
                            property="og:image"
                            content="https://www.asmaskiner.se/images/homepage.webp"
                          />

                          {/* Default Twitter Card */}
                          <meta name="twitter:card" content="summary_large_image" />
                        </Helmet>
                        {children}
                      </HelmetProvider>
                    </SiteContentProvider>
                  </TermsContextProvider>
                </FaqContextProvider>
              </NewsContextProvider>
            </JobsContextProvider>
          </MachineContextProvider>
        </UserContextProvider>
      </LayoutContextProvider>
    </StaticRenderContext.Provider>
  );
}

export default function App() {
  const router = useMemo(() => createBrowserRouter(routeConfig), []);
  return (
    <AppProviders>
      <RouterProvider router={router} />
    </AppProviders>
  );
}
